import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./Knowledge.css";
import {
  KnowledgeEntity,
  KnowledgeKind,
  KnowledgeStats,
  getKnowledgeStats,
  listKnowledgeEntities,
  rebuildKnowledge,
} from "../utils/api";
import { KIND_ORDER, entityDetail, kindPresentation, mentionsLabel } from "../utils/knowledge";

interface KnowledgeViewProps {
  onBack: () => void;
  onSelectEntity: (id: string) => void;
  /** Design review renders fixed data instead of calling the API. */
  initialEntities?: KnowledgeEntity[];
  initialStats?: KnowledgeStats;
  previewMode?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 40;

const KnowledgeView: React.FC<KnowledgeViewProps> = ({
  onBack,
  onSelectEntity,
  initialEntities,
  initialStats,
  previewMode = false,
}) => {
  const preview = previewMode && Boolean(initialEntities);
  const [entities, setEntities] = useState<KnowledgeEntity[]>(initialEntities ?? []);
  const [stats, setStats] = useState<KnowledgeStats | null>(initialStats ?? null);
  const [activeKind, setActiveKind] = useState<KnowledgeKind | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(!preview);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Typing should not fire a request per keystroke; the list is server-filtered.
  useEffect(() => {
    if (preview) return;
    const timer = window.setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, preview]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextEntities, nextStats] = await Promise.all([
        listKnowledgeEntities({
          query: debouncedSearch,
          kinds: activeKind ? [activeKind] : undefined,
          limit: PAGE_SIZE,
        }),
        getKnowledgeStats(),
      ]);
      setEntities(nextEntities);
      setStats(nextStats);
      // A full page means there is probably another one. Worst case the next
      // page comes back empty, which is cheaper than counting matches server-side.
      setHasMore(nextEntities.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [activeKind, debouncedSearch]);

  const loadMore = async () => {
    setLoadingMore(true);
    setError(null);
    try {
      const nextEntities = await listKnowledgeEntities({
        query: debouncedSearch,
        kinds: activeKind ? [activeKind] : undefined,
        limit: PAGE_SIZE,
        offset: entities.length,
      });
      setEntities((current) => [...current, ...nextEntities]);
      setHasMore(nextEntities.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (preview) return;
    void load();
  }, [load, preview]);

  const handleRebuild = async () => {
    if (preview) return;
    setRebuilding(true);
    setError(null);
    try {
      await rebuildKnowledge();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRebuilding(false);
    }
  };

  // Only kinds that exist are offered: a filter that can only return nothing is
  // not a choice worth showing.
  const availableKinds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of stats?.nodes_by_kind ?? []) counts.set(row.kind, row.count);
    return KIND_ORDER.filter((kind) => (counts.get(kind) ?? 0) > 0).map((kind) => ({
      kind,
      count: counts.get(kind) ?? 0,
    }));
  }, [stats]);

  const isEmptyLibrary = !loading && stats !== null && stats.total_nodes === 0;
  const isFiltered = Boolean(debouncedSearch.trim() || activeKind);

  return (
    <>
      <section className="app-section">
        <div className="app-card knowledge-header">
          <button type="button" className="back-button knowledge-back" onClick={onBack}>
            <i className="fas fa-arrow-left" aria-hidden="true" /> Back
          </button>
          <div className="knowledge-header-text">
            <h2 className="app-title knowledge-title">
              <i className="fas fa-diagram-project" aria-hidden="true" /> Knowledge
            </h2>
            {stats && (
              <p className="knowledge-subtitle">
                {stats.total_nodes.toLocaleString()} things from {stats.linked_links.toLocaleString()}{" "}
                {stats.linked_links === 1 ? "link" : "links"}
              </p>
            )}
          </div>
          <button
            type="button"
            className="app-button secondary-button knowledge-rebuild"
            onClick={handleRebuild}
            disabled={rebuilding || preview}
            title="Re-read every saved link and rebuild what was extracted"
          >
            <i className={`fas ${rebuilding ? "fa-spinner fa-spin" : "fa-rotate"}`} aria-hidden="true" />
            <span className="knowledge-rebuild-label">{rebuilding ? "Rebuilding…" : "Rebuild"}</span>
          </button>
        </div>
      </section>

      {error && (
        <section className="app-section">
          <div className="app-card knowledge-error">
            <i className="fas fa-triangle-exclamation" aria-hidden="true" /> {error}
          </div>
        </section>
      )}

      {isEmptyLibrary ? (
        <section className="app-section">
          <div className="app-card knowledge-empty-state">
            <i className="fas fa-diagram-project knowledge-empty-icon" aria-hidden="true" />
            <h3>Nothing collected yet</h3>
            <p>
              Places, songs, recipes and topics gather here as links are processed. Links saved
              before this existed can be read in now.
            </p>
            <button
              type="button"
              className="app-button primary-button"
              onClick={handleRebuild}
              disabled={rebuilding}
            >
              {rebuilding ? "Building…" : "Build from saved links"}
            </button>
          </div>
        </section>
      ) : (
        <section className="app-section">
          <div className="app-card">
            <div className="knowledge-search">
              <i className="fas fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search places, songs, topics…"
                aria-label="Search everything extracted"
              />
            </div>

            <div className="knowledge-kinds" role="group" aria-label="Filter by kind">
              <button
                type="button"
                className={`knowledge-kind-chip ${activeKind === null ? "is-active" : ""}`}
                onClick={() => setActiveKind(null)}
              >
                All
              </button>
              {availableKinds.map(({ kind, count }) => {
                const presentation = kindPresentation(kind);
                return (
                  <button
                    key={kind}
                    type="button"
                    className={`knowledge-kind-chip ${activeKind === kind ? "is-active" : ""}`}
                    onClick={() => setActiveKind(activeKind === kind ? null : kind)}
                  >
                    <i className={`fas ${presentation.icon}`} aria-hidden="true" />
                    {presentation.plural}
                    <span className="knowledge-kind-count">{count}</span>
                  </button>
                );
              })}
            </div>

            {loading ? (
              <p className="knowledge-status">Loading…</p>
            ) : entities.length === 0 ? (
              <p className="knowledge-status">
                {isFiltered ? "Nothing matches that." : "Nothing here yet."}
              </p>
            ) : (
              <>
                <ul className="knowledge-list">
                  {entities.map((entity) => (
                    <EntityRow key={entity.id} entity={entity} onSelect={onSelectEntity} />
                  ))}
                </ul>
                {hasMore && (
                  <div className="knowledge-more">
                    <button
                      type="button"
                      className="app-button secondary-button knowledge-more-button"
                      onClick={loadMore}
                      disabled={loadingMore || preview}
                    >
                      {loadingMore ? "Loading…" : "Show more"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}
    </>
  );
};

const EntityRow: React.FC<{ entity: KnowledgeEntity; onSelect: (id: string) => void }> = ({
  entity,
  onSelect,
}) => {
  const presentation = kindPresentation(entity.kind);
  const detail = entityDetail(entity);

  return (
    <li>
      <button type="button" className="knowledge-row" onClick={() => onSelect(entity.id)}>
        <span className={`knowledge-row-icon kind--${entity.kind}`}>
          <i className={`fas ${presentation.icon}`} aria-hidden="true" />
        </span>
        <span className="knowledge-row-text">
          <span className="knowledge-row-label">{entity.label}</span>
          <span className="knowledge-row-detail">{detail ?? presentation.label}</span>
        </span>
        <span className="knowledge-row-mentions">{mentionsLabel(entity.mentions)}</span>
      </button>
    </li>
  );
};

export default KnowledgeView;
